/**
 * RestaurantStatusHero — Setup Progress card on /dashboard.
 *
 * Simplified design (iter36): replaces the previous 5-tile checklist with a
 * high-level "Setup Progress" summary so the dashboard stops duplicating
 * the onboarding wizard. The wizard at /onboarding/setup is now the
 * SINGLE source of truth for setup completion.
 *
 * Two states (driven by `GET /api/onboarding/readiness`):
 *
 *   1. Not live yet  → "Setup Progress" card with progress bar + remaining
 *                       step labels (high-level only, not tiles) + a single
 *                       "Continue Setup" CTA → /onboarding/setup.
 *   2. Live           → Compact celebration banner with "View Dashboard" CTA.
 *
 * Step labels match the 8-section onboarding wizard ordering:
 *   1. Restaurant Information
 *   2. Logo & Branding
 *   3. Business Hours
 *   4. Holiday Hours        (informational — not gated by readiness API)
 *   5. Menu Categories
 *   6. Menu Items
 *   7. Photos & Gallery     (informational — not gated by readiness API)
 *   8. Publish Restaurant
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ChefHat, ArrowRight, Crown, Check } from 'lucide-react';
import { useReadinessRefresh } from '../utils/readinessBus';

const API = process.env.REACT_APP_BACKEND_URL;

// Step labels for the wizard — keys map back to readiness.sections
// returned by the backend. Items whose section is undefined in the
// readiness payload (Holiday Hours, Photos & Gallery) are surfaced
// as advisory copy only — they are not used for progress arithmetic.
const STEP_LABELS = [
  { key: 'profile',         label: 'Restaurant Information' },
  { key: 'logo',            label: 'Logo & Branding' },
  { key: 'business_hours',  label: 'Business Hours' },
  { key: 'categories',      label: 'Menu Categories' },
  { key: 'menu',            label: 'Menu Items' },
  { key: 'payouts',         label: 'Payouts Connected' },
  { key: 'publish',         label: 'Publish Restaurant' },
];

const RestaurantStatusHero = ({ token, restaurant, onReadiness }) => {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReadiness = useCallback(async () => {
    if (!token || !restaurant?.id) return;
    try {
      const r = await axios.get(`${API}/api/onboarding/readiness`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReadiness(r.data);
      onReadiness?.(r.data);
    } catch {
      /* silent — non-critical */
    } finally {
      setLoading(false);
    }
  }, [token, restaurant?.id, onReadiness]);

  useEffect(() => {
    if (!token || !restaurant?.id) return;
    fetchReadiness();
  }, [token, restaurant?.id, fetchReadiness]);

  // Listen for save events (Dashboard autosave, Setup wizard saves, etc.)
  // and tab visibility-change so the "Remaining steps" list always reflects
  // the current backend state — no more stale "❌ Phone Number Required"
  // badges after the owner saves the field.
  useReadinessRefresh(fetchReadiness);

  if (loading || !readiness) {
    return (
      <div
        data-testid="restaurant-status-hero-skeleton"
        className="mb-6 h-32 rounded-3xl border border-white/8 bg-white/[0.02] animate-pulse"
      />
    );
  }

  const isLive = !!readiness.is_live;
  const sections = readiness.sections || {};
  const percent = Math.max(0, Math.min(100, readiness.progress_percent || 0));
  const remaining = STEP_LABELS.filter((s) => !sections?.[s.key]?.ok);
  const completedCount = STEP_LABELS.length - remaining.length;

  // ─── LIVE state ───────────────────────────────────────────────────────
  if (isLive) {
    return (
      <div
        data-testid="restaurant-status-hero"
        data-status="live"
        className="mb-6 relative rounded-3xl border border-yellow-500/45 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-5 sm:p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.85)]"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <span
              data-testid="status-hero-label"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-yellow-500/40 bg-yellow-500/15 text-yellow-200 text-[10.5px] uppercase tracking-[0.22em] font-bold"
            >
              <Crown className="w-3 h-3 text-yellow-300" />
              Live on NoFee
            </span>
            <h2
              data-testid="status-hero-headline"
              className="mt-3 text-2xl sm:text-3xl font-black tracking-tight text-white leading-[1.15]"
            >
              You're live on NoFee.
            </h2>
            <p className="mt-1.5 text-[13px] text-white/55 leading-relaxed max-w-2xl">
              Customers can now order from your restaurant. QR code, share tools and orders are below.
            </p>
          </div>
          <Link
            to="/dashboard"
            data-testid="status-hero-cta"
            className="group inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[48px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[13.5px] tracking-tight transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5 whitespace-nowrap"
          >
            View Dashboard
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    );
  }

  // ─── PRE-LIVE: simplified Setup Progress card ────────────────────────
  return (
    <div
      data-testid="restaurant-status-hero"
      data-status={readiness.status || 'setup_required'}
      className="mb-6 relative rounded-3xl border border-amber-500/30 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-5 sm:p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.85)]"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          {/* Status pill */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span
              data-testid="status-hero-label"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-500/40 bg-amber-500/15 text-amber-200 text-[10.5px] uppercase tracking-[0.22em] font-bold"
            >
              <Sparkles className="w-3 h-3 text-amber-300" />
              Setup Progress
            </span>
            <span
              data-testid="status-hero-percent"
              className="text-[11.5px] text-white/55 font-bold tracking-tight"
            >
              {percent}% complete · {completedCount} of {STEP_LABELS.length} steps
            </span>
          </div>

          {/* Title + subtitle */}
          <h2
            data-testid="status-hero-headline"
            className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-[1.15]"
          >
            Complete your restaurant setup.
          </h2>
          <p className="mt-1.5 text-[13px] text-white/55 leading-relaxed max-w-2xl">
            Complete your restaurant setup to start receiving orders.
          </p>

          {/* Progress bar */}
          <div className="mt-4 h-1.5 rounded-full bg-white/[0.06] overflow-hidden" aria-hidden="true">
            <div
              data-testid="status-hero-progress-bar"
              className="h-full bg-gradient-to-r from-amber-300 via-amber-400 to-yellow-500 transition-all duration-700 ease-out"
              style={{ width: `${percent}%` }}
            />
          </div>

          {/* Remaining steps — text-only, high-level */}
          {remaining.length > 0 ? (
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-bold mb-2">
                Remaining steps
              </p>
              <ul
                data-testid="status-hero-remaining"
                className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5"
              >
                {remaining.map((s) => (
                  <li
                    key={s.key}
                    data-testid={`status-hero-remaining-${s.key}`}
                    className="flex items-center gap-2 text-[12.5px] text-white/75"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400/70 shrink-0" />
                    {s.label}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p
              data-testid="status-hero-ready-to-publish"
              className="mt-4 inline-flex items-center gap-2 text-[12.5px] text-emerald-300 font-bold"
            >
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
              All steps complete — ready to publish.
            </p>
          )}
        </div>

        {/* Single Continue Setup CTA */}
        <div className="shrink-0 sm:self-center w-full sm:w-auto">
          <Link
            to="/onboarding/setup"
            data-testid="status-hero-cta"
            className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3.5 min-h-[52px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[14px] tracking-tight transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5 whitespace-nowrap"
          >
            <ChefHat className="w-4 h-4" />
            Continue Setup
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RestaurantStatusHero;
